<template>
<div class="export-explaur-data">
  <h2>Écouvillons</h2>
  <alert type="info">
    <p>
      Les écouvillons sont téléchargés au <strong>format CSV</strong>.
    </p>
  </alert>
  <btn size="lg"
      class="export-button"
      @click="getData"
      type="reaExplaur"
      :disabled="waiting">
    RÉA-explAUR
  </btn>
  <save-blob :target="target"></save-blob>
</div>
</template>
<script>
import stringify from 'csv-stringify'
import moment from 'moment'
import saveBlob from '@/sui/components/saveblob'
import units from '@/sui/data/units'
import flatten from '@/sui/data/flatten/infections'
import csvColumns from '@/sui/data/csvColumns'
import sc from '@/sui/sc/explaur'
import assert from '@/sui/tools/assert'

export default {
  name: 'explaur-export',

  components: {
    'save-blob': saveBlob
  },

  props: {
    year: {
      type: String,
      required: true
    },
    surveillances: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      target: null,
      active: false
    }
  },

  computed: {
    waiting: function () {
      return this.active || this.surveillances.length === 0
    },
    version: function () {
      return parseInt(this.year) - 2018
    }
  },

  methods: {
    getData: function () {
      const requestSwabs = {
        year: parseInt(this.year)
      }
      const requestInf = {
        y: parseInt(this.year),
        inf: 'bact',
        dset: `SPIADI${this.year}`
      }
      const vm = this
      this.active = true
      const result = {}

      Promise.all([
        this.$store.dispatch('explaur/all', requestSwabs),
        this.$store.dispatch('infections/all', requestInf)
      ]).then(function (r) {
        for (let i = 0; i < r.length; i++) {
          for (const d in r[i].data) {
            if (d === 'srvs' && r[i].data[d].length === 0) {
              vm.setError('Aucun écouvillon présent dans la base')
              return result
            }
            result[d] = r[i].data[d]
          }
        }
        vm.exportList(result)
      }).catch(function (error) {
        vm.setError(error)
      })
    },

    exportList: function (list) {
      const result = []
      const fBacts = []
      const vm = this
      const yunits = units[this.year]
      const ysurv = this.surveillances[this.year]

      const patientFields = sc.patientFields(this.year)
      const unitFields = sc.unitFields(this.year)

      function fillFromFields (result, prefix, fields, data) {
        const model = {}
        for (let i = 0; i !== fields.length; ++i) {
          const f = fields[i]
          model[f.key] = { error: '', value: data[f.key] }
        }

        for (let i = 0; i !== fields.length; ++i) {
          const f = fields[i]
          if (f.show == null || f.show(model)) {
            result[`${prefix}${f.key}`] = model[f.key].value
          }
        }
        return result
      }

      for (let i = 0; i !== list.srvs.length; ++i) {
        const s = list.srvs[i]
        const surv = ysurv.find(e => e.id === s.surv)
        const finess = surv.establishment.finess

        for (let j = 0; j !== s.srvs.length; ++j) {
          const sr = s.srvs[j]
          const code = yunits.findFromIndex(sr.uf.ufType).code
          const label = String.fromCharCode(sr.labl)

          if (sr.stat.current.value === 0) {
            continue
          }

          const newItem = () => {
            const result = {
              name: surv.establishment.name,
              finess: surv.establishment.finess,
              swuf: sr.uf.name,
              code: code
            }

            fillFromFields(result, '', unitFields, sr.prct)
            return result
          }

          let hasSwab = false
          for (let k = 0; k !== sr.epds.length; ++k) {
            const ep = sr.epds[k]
            const item = newItem()
            const swabLabel = `${finess}-${label}${k + 1}`
            item.status = ep.stat.current.value
            item.label = swabLabel
            item.creno = ep.crnb !== 0 ? ep.crnb : null

            if (item.status > 0) {
              fillFromFields(item, 'w', patientFields, ep.prms)

              const bact = list.v.find(r => {
                for (const l of r.inf.microOrganisms) {
                  if (l.explaurID === item.label) {
                    return true
                  }
                }
                return false
              })

              if (bact != null) {
                const fBact = flatten.createExportedInfections(
                  bact,
                  'bact',
                  vm.version
                )

                Object.assign(item, fBact)
                fBacts.push(fBact)
              }
            }
            result.push(item)
            hasSwab = true
          }

          if (!hasSwab) {
            result.push(newItem())
          }
        }
      }

      function unexpectedValue (column, value) {
        assert(false, `Unexpected value for column «${column}»: ${value}`)
        return JSON.stringify(value)
      }

      stringify(
        result,
        {
          header: true,
          delimiter: ';',
          bom: true,
          cast: {
            number: function (n, ctx) {
              if (n == null) {
                return null
              }

              if (ctx.column === 'wsex') {
                switch (n) {
                  case 1: {
                    return 'Masculin'
                  }
                  case 2: {
                    return 'Féminin'
                  }
                  case 3: {
                    return 'Autre'
                  }
                  case 9: {
                    return 'Non connu'
                  }
                }

                return unexpectedValue(ctx.column, n)
              }

              if (ctx.column === 'wedlm') {
                switch (n) {
                  case 1: {
                    return '<= 7 jours'
                  }
                  case 2: {
                    return '> 7 jours'
                  }
                  case 3: {
                    return 'Non connu'
                  }
                }
                return unexpectedValue(ctx.column, n)
              }

              if (ctx.column === 'status') {
                switch (n) {
                  case 0: {
                    return 'Fiche patient non remplie'
                  }
                  case 1: {
                    return 'Non envoyé'
                  }
                  case 2: {
                    return `En cours d'acheminement`
                  }
                  case 3: {
                    return `Reçu au laboratoire`
                  }
                }
                return unexpectedValue(ctx.column, n)
              }

              const numeric = [
                'beds', 'iktf', 'ipdf', 'ispf', 'ictf',
                'edlf', 'edld', 'istf', 'ilof', 'iwat', 'iwaf', 'ioif',
                'creno', 'wiwat', 'wpadr'
              ]

              if (
                numeric.indexOf(ctx.column) !== -1 ||
                ctx.column.startsWith('pat_') ||
                ctx.column.startsWith('inf_') ||
                ctx.column.startsWith('delay_') ||
                ctx.column === 'type' ||
                ctx.column === 'sheet_ver'
              ) {
                return JSON.stringify(n)
              }

              switch (n) {
                case 0: {
                  return ''
                }
                case 1: {
                  return 'Oui'
                }
                case 2: {
                  return 'Non'
                }
                case 9: {
                  return 'Non connu'
                }
              }

              return unexpectedValue(ctx.column, n)
            },

            string: function (s, ctx) {
              if (ctx.column === 'wepdt' ||
                  ctx.column === 'wbidt') {
                return moment(s).format('DD/MM/YYYY')
              }
              if (s === '0') {
                return ''
              } else {
                return s
              }
            }
          },
          columns: this.makeColumns(csvColumns.swabs, fBacts)
        },

        function (err, output) {
          if (err != null) {
            vm.$notify({
              type: 'danger',
              title: 'Erreur !',
              content: `Impossible d'exporter les données: ${err}.`
            })
          } else {
            setTimeout(
              function () {
                const date = moment().format('DD-MM-YYYY-HH-mm-ss')
                vm.active = false
                vm.target = {
                  filename: `SPIADI${vm.year} - RÉA-EXPLAUR - ÉCOUVILLONS - ${date}.csv`,
                  blob: new Blob([output], { type: 'text/plain' })
                }
              }, 10)
          }
        }
      )
    },

    setError: function (err) {
      this.active = false
      this.$notify({
        type: 'danger',
        title: 'Erreur !',
        content: `Impossible d'exporter les données: ${err}.`
      })
    },

    makeColumns: function (sc, bc) {
      if (bc.length !== 0) {
        const infColumns = flatten.getInfectionsColumns(bc)
        return sc.concat(infColumns)
      } else {
        return sc
      }
    }
  }
}
</script>
<style scoped lang="less">
.export-infections-data {
  .export-button {
    margin: 0.3rem;
  }
}
</style>
